/* ==========================================================================
   Theme toggle
   ========================================================================== */

(() => {
	const storageKey = "andersondev-theme";
	const root = document.documentElement;
	const toggle = document.querySelector("[data-theme-toggle]");

	const getSystemTheme = () => {
		return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
	};

	const getStoredTheme = () => {
		try {
			return window.localStorage.getItem(storageKey);
		} catch (error) {
			return null;
		}
	};

	const setStoredTheme = (theme) => {
		try {
			window.localStorage.setItem(storageKey, theme);
		} catch (error) {
			// Ignore private browsing/storage errors.
		}
	};

	const applyTheme = (theme) => {
		const resolvedTheme = theme === "system" || !theme ? getSystemTheme() : theme;
		root.setAttribute("data-theme", resolvedTheme);

		if (toggle) {
			toggle.setAttribute("aria-pressed", resolvedTheme === "dark" ? "true" : "false");
		}
	};

	const initialTheme = getStoredTheme() || "light";
	applyTheme(initialTheme);

	if (toggle) {
		toggle.addEventListener("click", () => {
			const current = root.getAttribute("data-theme") || "light";
			const next = current === "dark" ? "light" : "dark";

			setStoredTheme(next);
			applyTheme(next);
		});
	}
})();

/* ==========================================================================
   Mobile menu
   ========================================================================== */

(() => {
	const root = document.documentElement;
	const toggle = document.querySelector("[data-menu-toggle]");
	const menu = document.querySelector("[data-primary-menu]");
	const closeButton = document.querySelector("[data-menu-close]");
	const overlay = document.querySelector("[data-menu-overlay]");

	if (!toggle || !menu || !overlay) {
		return;
	}

	const mobileQuery = window.matchMedia("(max-width: 1180px)");
	const submenuItems = Array.from(menu.querySelectorAll(".menu-item-has-children"));

	submenuItems.forEach((item, index) => {
		const link = item.querySelector(":scope > a");
		const submenu = item.querySelector(":scope > .sub-menu");

		if (!link || !submenu || item.querySelector(":scope > [data-submenu-toggle]")) {
			return;
		}

		const submenuId = submenu.id || `inventiva-mobile-submenu-${index}`;
		submenu.id = submenuId;

		const button = document.createElement("button");
		button.className = "inventiva-site-header__submenu-toggle";
		button.type = "button";
		button.setAttribute("aria-expanded", "false");
		button.setAttribute("aria-controls", submenuId);
		button.setAttribute(
			"aria-label",
			`${link.textContent.trim() || "Submenu"} submenu`
		);
		button.setAttribute("data-submenu-toggle", "");
		button.innerHTML = '<span aria-hidden="true"></span>';

		link.insertAdjacentElement("afterend", button);
		submenu.setAttribute("aria-hidden", "true");

		button.addEventListener("click", () => {
			const isOpen = item.classList.contains("is-submenu-open");

			item.classList.toggle("is-submenu-open", !isOpen);
			button.setAttribute("aria-expanded", isOpen ? "false" : "true");
			submenu.setAttribute("aria-hidden", isOpen ? "true" : "false");
		});
	});

	const resetSubmenus = () => {
		const isMobile = mobileQuery.matches;

		submenuItems.forEach((item) => {
			const button = item.querySelector(":scope > [data-submenu-toggle]");
			const submenu = item.querySelector(":scope > .sub-menu");

			item.classList.remove("is-submenu-open");

			if (button) {
				button.setAttribute("aria-expanded", "false");
			}

			if (submenu) {
				if (isMobile) {
					submenu.setAttribute("aria-hidden", "true");
				} else {
					submenu.removeAttribute("aria-hidden");
				}
			}
		});
	};

	const setMenuState = (isOpen) => {
		const shouldOpen = isOpen && mobileQuery.matches;

		root.classList.toggle("inventiva-menu-open", shouldOpen);
		toggle.setAttribute("aria-expanded", shouldOpen ? "true" : "false");

		if (mobileQuery.matches) {
			menu.setAttribute("aria-hidden", shouldOpen ? "false" : "true");
		} else {
			menu.removeAttribute("aria-hidden");
		}

		if (!shouldOpen) {
			resetSubmenus();
		}
	};

	window.inventivaCloseMobileMenu = () => setMenuState(false);

	toggle.addEventListener("click", () => {
		setMenuState(!root.classList.contains("inventiva-menu-open"));
	});

	menu.addEventListener("click", (event) => {
		const link = event.target.closest("a");

		if (!link || !mobileQuery.matches) {
			return;
		}

		const href = link.getAttribute("href");

		if (!href || href === "#") {
			return;
		}

		window.setTimeout(() => setMenuState(false), 0);
	});

	[closeButton, overlay].forEach((control) => {
		if (!control) {
			return;
		}

		control.addEventListener("click", () => setMenuState(false));
	});

	document.addEventListener("keydown", (event) => {
		if (event.key === "Escape") {
			setMenuState(false);
		}
	});

	mobileQuery.addEventListener("change", () => {
		setMenuState(root.classList.contains("inventiva-menu-open"));
	});

	setMenuState(false);
})();

/* ==========================================================================
   Header scroll state
   ========================================================================== */

(() => {
	const header = document.querySelector(".inventiva-site-header");

	if (!header) {
		return;
	}

	const updateHeaderState = () => {
		header.classList.toggle("is-scrolled", window.scrollY >= 100);
	};

	updateHeaderState();
	window.addEventListener("scroll", updateHeaderState, { passive: true });
})();

/* ==========================================================================
   Smooth anchor scrolling
   ========================================================================== */

(() => {
	const header = document.querySelector(".inventiva-site-header");

	const getHeaderOffset = () => {
		return header ? header.getBoundingClientRect().height : 0;
	};

	document.addEventListener("click", (event) => {
		const link = event.target.closest("a[href]");

		if (!link) {
			return;
		}

		let url = null;

		try {
			url = new URL(link.getAttribute("href"), window.location.href);
		} catch (error) {
			return;
		}

		const currentPath = window.location.pathname.replace(/\/+$/, "/");
		const linkPath = url.pathname.replace(/\/+$/, "/");
		const href = url.hash;

		if (
			!href ||
			href === "#" ||
			url.origin !== window.location.origin ||
			linkPath !== currentPath ||
			url.search !== window.location.search
		) {
			return;
		}

		let target = null;

		try {
			target = document.querySelector(href);
		} catch (error) {
			return;
		}

		if (!target) {
			return;
		}

		event.preventDefault();

		const top =
			target.getBoundingClientRect().top +
			window.pageYOffset -
			getHeaderOffset() -
			24;

		window.scrollTo({
			top,
			behavior: "smooth",
		});

		if (history.pushState) {
			history.pushState(null, "", `${url.pathname}${url.search}${href}`);
		}

		if (typeof window.inventivaCloseMobileMenu === "function") {
			window.inventivaCloseMobileMenu();
		}
	});
})();

/* ==========================================================================
   Inventiva leadership hover blob
   ========================================================================== */

(() => {
	const grids = document.querySelectorAll("[data-inventiva-leadership-grid]");

	if (!grids.length) {
		return;
	}

	const blobStyles = [
		{
			color: "var(--inventiva-lime)",
			radius: "62% 38% 70% 30% / 46% 54% 46% 54%",
			rotate: "-14deg",
		},
		{
			color: "var(--inventiva-aqua)",
			radius: "48% 52% 38% 62% / 58% 44% 56% 42%",
			rotate: "12deg",
		},
		{
			color: "var(--inventiva-mint, #b0f5c0)",
			radius: "72% 28% 52% 48% / 42% 64% 36% 58%",
			rotate: "-6deg",
		},
		{
			color: "var(--inventiva-lime)",
			radius: "42% 58% 64% 36% / 58% 40% 60% 42%",
			rotate: "18deg",
		},
	];

	const blobColors = [
		"var(--inventiva-lime)",
		"var(--inventiva-aqua)",
		"var(--inventiva-mint, #b0f5c0)",
		"var(--inventiva-lime)",
	];

	grids.forEach((grid) => {
		const cards = grid.querySelectorAll(".inventiva-leadership-card");

		if (!cards.length) {
			return;
		}

		const moveBlob = (card) => {
			const gridRect = grid.getBoundingClientRect();
			const image = card.querySelector(".inventiva-leadership-card__image-wrap");

			if (!image) {
				return;
			}

			const imageRect = image.getBoundingClientRect();
			const styleIndex = Number(card.getAttribute("data-blob-index") || 0);
			const color = blobColors[styleIndex % blobColors.length];

			const size = Math.max(imageRect.width * 1.36, 150);
			const x = imageRect.left - gridRect.left - size * 0.12;
			const y = imageRect.top - gridRect.top - size * 0.18;

			grid.style.setProperty("--blob-x", `${x}px`);
			grid.style.setProperty("--blob-y", `${y}px`);
			grid.style.setProperty("--blob-size", `${size}px`);
			grid.style.setProperty("--blob-color", color);

			grid.classList.add("is-blob-active");
		};

		cards.forEach((card) => {
			card.addEventListener("mouseenter", () => moveBlob(card));
			card.addEventListener("focusin", () => moveBlob(card));
		});

		grid.addEventListener("mouseleave", () => {
			grid.classList.remove("is-blob-active");
		});

		window.addEventListener("resize", () => {
			const activeCard = grid.querySelector(
				".inventiva-leadership-card:hover, .inventiva-leadership-card:focus-within"
			);

			if (activeCard) {
				moveBlob(activeCard);
			}
		});
	});
})();

/* ==========================================================================
   Inventiva history timeline
   ========================================================================== */

(() => {
	const initInventivaHistoryTimeline = () => {
		const timelines = document.querySelectorAll("[data-inventiva-history]");

		if (!timelines.length) {
			return;
		}

		timelines.forEach((timeline) => {
			const buttons = Array.from(timeline.querySelectorAll("[data-history-index]"));
			const panels = Array.from(timeline.querySelectorAll("[data-history-panel]"));
			const mediaItems = Array.from(timeline.querySelectorAll("[data-history-media]"));
			const indicator = timeline.querySelector(".inventiva-history-timeline__year-indicator");
			const nav = timeline.querySelector(".inventiva-history-timeline__year-nav");
			const prev = timeline.querySelector("[data-history-prev]");
			const next = timeline.querySelector("[data-history-next]");

			if (!buttons.length || !panels.length || !indicator || !nav) {
				return;
			}

			let activeIndex = buttons.findIndex((button) => button.classList.contains("is-active"));

			if (activeIndex < 0) {
				activeIndex = 0;
			}

			nav.style.setProperty("--history-count", buttons.length);

			const moveIndicator = () => {
				const activeButton = buttons[activeIndex];

				if (!activeButton) {
					return;
				}

				const navRect = nav.getBoundingClientRect();
				const buttonRect = activeButton.getBoundingClientRect();
				const indicatorWidth = Math.min(buttonRect.width * 0.42, 42);
				const left =
					buttonRect.left -
					navRect.left +
					buttonRect.width / 2 -
					indicatorWidth / 2;

				indicator.style.width = `${indicatorWidth}px`;
				indicator.style.transform = `translate3d(${left}px, 0, 0)`;
			};

			const activate = (index, shouldFocus = false) => {
				if (index < 0) {
					index = buttons.length - 1;
				}

				if (index >= buttons.length) {
					index = 0;
				}

				activeIndex = index;

				buttons.forEach((button, buttonIndex) => {
					const isActive = buttonIndex === activeIndex;

					button.classList.toggle("is-active", isActive);
					button.setAttribute("aria-selected", isActive ? "true" : "false");
					button.setAttribute("tabindex", isActive ? "0" : "-1");

					if (isActive && shouldFocus) {
						button.focus();
					}
				});

				panels.forEach((panel, panelIndex) => {
					const isActive = panelIndex === activeIndex;

					panel.classList.toggle("is-active", isActive);
					panel.hidden = !isActive;
				});

				mediaItems.forEach((media, mediaIndex) => {
					const isActive = mediaIndex === activeIndex;

					media.classList.toggle("is-active", isActive);
					media.hidden = !isActive;
				});

				moveIndicator();
			};

			buttons.forEach((button, index) => {
				button.addEventListener("click", () => {
					activate(index);
				});

				button.addEventListener("keydown", (event) => {
					if (event.key === "ArrowRight") {
						event.preventDefault();
						activate(activeIndex + 1, true);
					}

					if (event.key === "ArrowLeft") {
						event.preventDefault();
						activate(activeIndex - 1, true);
					}

					if (event.key === "Home") {
						event.preventDefault();
						activate(0, true);
					}

					if (event.key === "End") {
						event.preventDefault();
						activate(buttons.length - 1, true);
					}
				});
			});

			if (prev) {
				prev.addEventListener("click", () => {
					activate(activeIndex - 1);
				});
			}

			if (next) {
				next.addEventListener("click", () => {
					activate(activeIndex + 1);
				});
			}

			window.addEventListener("resize", moveIndicator);

			requestAnimationFrame(() => {
				activate(activeIndex);
			});
		});
	};

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initInventivaHistoryTimeline);
	} else {
		initInventivaHistoryTimeline();
	}
})();


/* ==========================================================================
   Inventiva hero and feature animations
   ========================================================================== */

(() => {
	if (typeof gsap === "undefined") {
		return;
	}

	const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

	const splitWords = (element, className = "inventiva-split-word") => {
		if (!element) {
			return [];
		}

		if (element.dataset.splitReady === "true") {
			return Array.from(element.querySelectorAll(`.${className}`));
		}

		const originalHtml = element.innerHTML.trim();

		const parts = originalHtml
			.replace(/<br\s*\/?>/gi, " <br> ")
			.split(/\s+/)
			.filter(Boolean);

		const html = parts
			.map((part) => {
				if (part.toLowerCase() === "<br>") {
					return "<br>";
				}

				return `<span class="${className}">${part}</span>`;
			})
			.join('<span class="inventiva-split-space"> </span>');

		element.innerHTML = html;
		element.dataset.splitReady = "true";

		return Array.from(element.querySelectorAll(`.${className}`));
	};

	const createScrollTrigger = (trigger, animation, start = "top 78%") => {
		if (typeof ScrollTrigger !== "undefined") {
			ScrollTrigger.create({
				trigger,
				start,
				once: true,
				onEnter: () => animation.play(),
			});
		} else {
			animation.play();
		}
	};

	/* ----------------------------------------------------------------------
	   Home hero
	   ---------------------------------------------------------------------- */

	const homeHero = document.querySelector("[data-inventiva-home-hero]");

	if (homeHero) {
		const blob =
			homeHero.querySelector(".inventiva-home-hero__art-reveal") ||
			homeHero.querySelector(".inventiva-home-hero__art");

		const shapeLayer =
			homeHero.querySelector(".inventiva-home-hero__mask-layer") ||
			blob;

		const bandGroup = homeHero.querySelector(".inventiva-home-hero__bands");
		const bands = Array.from(homeHero.querySelectorAll(".inventiva-home-hero__band"));
		const secondaryBlob = homeHero.querySelector(".inventiva-home-hero__secondary-blob");

		const eyebrow = homeHero.querySelector(".inventiva-home-hero__eyebrow");
		const heading = homeHero.querySelector(".inventiva-home-hero__heading");
		const bodyText = homeHero.querySelector(".inventiva-home-hero__text");
		const actions = homeHero.querySelector(".inventiva-home-hero__actions");
		const headingWords = splitWords(heading);

		if (blob && shapeLayer && bands.length) {
			if (bandGroup) {
				gsap.set(bandGroup, {
					attr: {
						transform: "translate(-735 0) scale(0.4558)",
					},
				});
			}

			const startAmbientBands = () => {
				bands.forEach((band, index) => {
					if (index === 0) {
						return;
					}

					const direction = index % 2 === 0 ? 1 : -1;
					const x = direction * (34 + index * 11);
					const y = -direction * (18 + index * 7);
					const duration = 3.8 + index * 0.45;

					gsap.to(band, {
						x,
						y,
						duration,
						ease: "sine.inOut",
						yoyo: true,
						repeat: -1,
					});
				});
			};

			if (reduceMotion) {
				homeHero.classList.add("is-hero-js-ready");
				homeHero.classList.remove("is-hero-preload");

				gsap.set([blob, secondaryBlob], {
					autoAlpha: 1,
					filter: "blur(0px)",
					clearProps: "transform",
				});

				gsap.set(shapeLayer, {
					scaleX: 1,
					scaleY: 1,
					skewX: 0,
					rotate: 0,
				});

				gsap.set(bands, {
					autoAlpha: 1,
					x: 0,
					y: 0,
					scale: 1,
				});

				gsap.set([eyebrow, heading, bodyText, actions], {
					autoAlpha: 1,
					y: 0,
					filter: "blur(0px)",
				});

				startAmbientBands();
				return;
			}

			gsap.set(blob, {
				autoAlpha: 0,
				filter: "blur(28px)",
			});

			gsap.set(shapeLayer, {
				scaleX: 0.92,
				scaleY: 1.09,
				skewX: -4,
				rotate: -1.4,
				transformOrigin: "48% 52%",
			});

			if (secondaryBlob) {
				gsap.set(secondaryBlob, {
					autoAlpha: 0,
					x: 70,
					y: 30,
					scale: 0.84,
					rotate: 8,
					filter: "blur(14px)",
				});
			}

			bands.forEach((band, index) => {
				if (index === 0) {
					gsap.set(band, {
						autoAlpha: 0.72,
						x: 0,
						y: 0,
						scale: 1,
					});
					return;
				}

				const direction = index % 2 === 0 ? 1 : -1;
				const verticalDirection = index % 3 === 0 ? -1 : 1;

				gsap.set(band, {
					autoAlpha: 0,
					x: direction * (180 + index * 28),
					y: verticalDirection * (70 + index * 12),
					scale: 1.015,
				});
			});

			gsap.set([eyebrow, bodyText, actions], {
				autoAlpha: 0,
				y: 18,
				filter: "blur(8px)",
			});

			gsap.set(headingWords.length ? headingWords : heading, {
				autoAlpha: 0,
				yPercent: 110,
				filter: "blur(10px)",
			});

			homeHero.classList.add("is-hero-js-ready");
			homeHero.classList.remove("is-hero-preload");

			const tl = gsap.timeline({
				defaults: {
					ease: "power3.out",
				},
				onComplete: () => {
					homeHero.classList.add("is-hero-reveal-complete");
					startAmbientBands();
				},
			});

			tl.to(blob, {
				autoAlpha: 1,
				filter: "blur(0px)",
				duration: 1.65,
				ease: "power3.out",
			})
				.to(
					shapeLayer,
					{
						scaleX: 1.035,
						scaleY: 0.975,
						skewX: 1.6,
						rotate: 0.42,
						duration: 2.2,
						ease: "power3.out",
					},
					0
				)
				.to(
					bands,
					{
						autoAlpha: (index) => {
							if (index === 0) {
								return 1;
							}

							if (index <= 2) {
								return 0.92;
							}

							return 0.52;
						},
						x: 0,
						y: 0,
						scale: 1,
						duration: 4.8,
						stagger: {
							each: 0.22,
							from: "start",
						},
						ease: "expo.out",
					},
					0.35
				)
				.to(
					shapeLayer,
					{
						scaleX: 1,
						scaleY: 1,
						skewX: 0,
						rotate: 0,
						duration: 3.4,
						ease: "sine.inOut",
					},
					1.05
				)
				.to(
					secondaryBlob,
					{
						autoAlpha: 1,
						x: 0,
						y: 0,
						scale: 1,
						rotate: 0,
						filter: "blur(0px)",
						duration: 1.55,
						ease: "power3.out",
					},
					1.1
				)
				.to(
					eyebrow,
					{
						autoAlpha: 1,
						y: 0,
						filter: "blur(0px)",
						duration: 1.35,
						ease: "power3.out",
					},
					2.15
				)
				.to(
					headingWords.length ? headingWords : heading,
					{
						autoAlpha: 1,
						yPercent: 0,
						filter: "blur(0px)",
						duration: 2.1,
						stagger: 0.105,
						ease: "power4.out",
					},
					2.35
				)
				.to(
					bodyText,
					{
						autoAlpha: 1,
						y: 0,
						filter: "blur(0px)",
						duration: 0.9,
						ease: "power3.out",
					},
					3.1
				)
				.to(
					actions,
					{
						autoAlpha: 1,
						y: 0,
						filter: "blur(0px)",
						duration: 0.75,
						ease: "power3.out",
					},
					3.35
				);
		}
	}

	/* ----------------------------------------------------------------------
	   Page hero / inside header
	   ---------------------------------------------------------------------- */

	document.querySelectorAll("[data-inventiva-page-hero]").forEach((hero) => {
		const media = hero.querySelector(".inventiva-page-hero__media");
		const image = hero.querySelector(".inventiva-page-hero__image");
		const leaf = hero.querySelector(".inventiva-page-hero__leaf");
		const eyebrow = hero.querySelector(".inventiva-page-hero__eyebrow");
		const heading = hero.querySelector(".inventiva-page-hero__heading");
		const text = hero.querySelector(".inventiva-page-hero__text");
		const line = hero.querySelector(".inventiva-page-hero__line");
		const headingWords = splitWords(heading);

		const contentItems = [eyebrow, text].filter(Boolean);

		if (reduceMotion) {
			hero.classList.remove("is-page-hero-preload");
			gsap.set([media, image, leaf, eyebrow, heading, text, line], {
				autoAlpha: 1,
				x: 0,
				y: 0,
				scale: 1,
				filter: "blur(0px)",
				clearProps: "transform",
			});
			gsap.set(line, { scaleX: 1 });
			return;
		}

		if (media) {
			gsap.set(media, {
				autoAlpha: 0,
				x: 90,
				y: 22,
				scaleX: 0.94,
				scaleY: 1.06,
				rotate: 1.2,
				filter: "blur(24px)",
				transformOrigin: "52% 50%",
			});
		}

		if (leaf) {
			gsap.set(leaf, {
				autoAlpha: 0,
				x: -42,
				y: 24,
				scale: 0.88,
				rotate: -8,
				filter: "blur(12px)",
			});
		}

		gsap.set(contentItems, {
			autoAlpha: 0,
			y: 18,
			filter: "blur(8px)",
		});

		gsap.set(headingWords.length ? headingWords : heading, {
			autoAlpha: 0,
			yPercent: 110,
			filter: "blur(10px)",
		});

		if (line) {
			gsap.set(line, {
				scaleX: 0,
				autoAlpha: 1,
			});
		}

		hero.classList.remove("is-page-hero-preload");

		const tl = gsap.timeline({
			paused: true,
			defaults: {
				ease: "power3.out",
			},
		});

		tl.to(
			media,
			{
				autoAlpha: 1,
				x: 0,
				y: 0,
				scaleX: 1,
				scaleY: 1,
				rotate: 0,
				filter: "blur(0px)",
				duration: 1.8,
				ease: "power3.out",
			},
			0
		)
			.to(
				media,
				{
					scaleX: 1,
					scaleY: 1,
					duration: 2.3,
					ease: "sine.inOut",
				},
				0.85
			)
			.to(
				leaf,
				{
					autoAlpha: 1,
					x: 0,
					y: 0,
					scale: 1,
					rotate: 0,
					filter: "blur(0px)",
					duration: 1.35,
					ease: "power3.out",
				},
				0.45
			)
			.to(
				eyebrow,
				{
					autoAlpha: 1,
					y: 0,
					filter: "blur(0px)",
					duration: 1.1,
					ease: "power3.out",
				},
				0.55
			)
			.to(
				headingWords.length ? headingWords : heading,
				{
					autoAlpha: 1,
					yPercent: 0,
					filter: "blur(0px)",
					duration: 1.55,
					stagger: 0.075,
					ease: "power4.out",
				},
				0.72
			)
			.to(
				text,
				{
					autoAlpha: 1,
					y: 0,
					filter: "blur(0px)",
					duration: 0.85,
					ease: "power3.out",
				},
				1.38
			)
			.to(
				line,
				{
					scaleX: 1,
					duration: 0.95,
					ease: "power3.out",
				},
				1.55
			);

		createScrollTrigger(hero, tl, "top 78%");
	});

	/* ----------------------------------------------------------------------
	   Feature band
	   ---------------------------------------------------------------------- */

	document.querySelectorAll(".inventiva-feature").forEach((feature) => {
		const media = feature.querySelector(".inventiva-feature__media");
		const image = feature.querySelector(".inventiva-feature__image");
		const content = feature.querySelector(".inventiva-feature__content");
		const heading = feature.querySelector(".inventiva-feature__heading");
		const text = feature.querySelector(".inventiva-feature__text");
		const actions = feature.querySelector(".inventiva-feature__actions");
		const headingWords = splitWords(heading);

		if (reduceMotion) {
			gsap.set([media, image, content, heading, text, actions], {
				autoAlpha: 1,
				filter: "blur(0px)",
			});

			gsap.set([image, content, heading, text, actions], {
				x: 0,
				y: 0,
				clearProps: "transform",
			});
			return;
		}

		const isEven = Array.from(feature.parentNode.children).indexOf(feature) % 2 === 1;
		const contentStartX = isEven ? 34 : -34;

		/*
		 * Do not animate x/y on .inventiva-feature__media.
		 * That element already uses CSS transforms for layout positioning.
		 */
		gsap.set(media, {
			autoAlpha: 0,
			filter: "blur(18px)",
		});

		gsap.set(image, {
			scale: 1.035,
			x: isEven ? -34 : 34,
			y: 18,
			transformOrigin: "50% 50%",
		});

		gsap.set(content, {
			autoAlpha: 1,
		});

		gsap.set(headingWords.length ? headingWords : heading, {
			autoAlpha: 0,
			yPercent: 105,
			filter: "blur(10px)",
		});

		gsap.set([text, actions], {
			autoAlpha: 0,
			x: contentStartX,
			y: 14,
			filter: "blur(8px)",
		});

		const tl = gsap.timeline({
			paused: true,
			defaults: {
				ease: "power3.out",
			},
		});

		tl.to(
			media,
			{
				autoAlpha: 1,
				filter: "blur(0px)",
				duration: 1.25,
				ease: "power3.out",
			},
			0
		)
			.to(
				image,
				{
					scale: 1,
					x: 0,
					y: 0,
					duration: 1.8,
					ease: "sine.inOut",
				},
				0
			)
			.to(
				headingWords.length ? headingWords : heading,
				{
					autoAlpha: 1,
					yPercent: 0,
					filter: "blur(0px)",
					duration: 1.15,
					stagger: 0.055,
					ease: "power4.out",
				},
				0.18
			)
			.to(
				text,
				{
					autoAlpha: 1,
					x: 0,
					y: 0,
					filter: "blur(0px)",
					duration: 0.85,
					ease: "power3.out",
				},
				0.75
			)
			.to(
				actions,
				{
					autoAlpha: 1,
					x: 0,
					y: 0,
					filter: "blur(0px)",
					duration: 0.75,
					ease: "power3.out",
				},
				0.95
			);

		createScrollTrigger(feature, tl, "top 78%");
	});
})();

/* ==========================================================================
   Homepage first icon intro helper
   ========================================================================== */

(() => {
	const body = document.body;

	if (!body || (!body.classList.contains("home") && !body.classList.contains("front-page"))) {
		return;
	}

	const firstIconIntro = document.querySelector(".inventiva-icon-intro");

	if (!firstIconIntro) {
		return;
	}

	firstIconIntro.classList.add("inventiva-icon-intro--home-first");
})();

/* ==========================================================================
   Inventiva leadership single animation
   ========================================================================== */

(() => {
	if (typeof gsap === "undefined") {
		return;
	}

	const hero = document.querySelector(".inventiva-leadership-single__hero");

	if (!hero) {
		return;
	}

	const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

	const blob = hero.querySelector(".inventiva-leadership-single__blob");
	const imageWrap = hero.querySelector(".inventiva-leadership-single__image-wrap");
	const image = hero.querySelector(".inventiva-leadership-single__image");
	const name = hero.querySelector(".inventiva-leadership-single__name");
	const title = hero.querySelector(".inventiva-leadership-single__title");
	const bio = hero.querySelector(".inventiva-leadership-single__bio");
	const actions = hero.querySelector(".inventiva-leadership-single__actions");
	const nav = hero.querySelectorAll(".inventiva-leadership-single__nav");

	if (reduceMotion) {
		gsap.set([blob, imageWrap, image, name, title, bio, actions, nav], {
			autoAlpha: 1,
			x: 0,
			y: 0,
			scale: 1,
			filter: "blur(0px)",
			clearProps: "transform",
		});

		return;
	}

	gsap.set(blob, {
		autoAlpha: 0,
		x: -42,
		y: 18,
		scale: 0.88,
		rotate: -4,
		filter: "blur(18px)",
		transformOrigin: "50% 50%",
	});

	gsap.set(imageWrap, {
		autoAlpha: 0,
		x: -24,
		y: 16,
		scale: 0.96,
		filter: "blur(14px)",
		transformOrigin: "50% 50%",
	});

	gsap.set(image, {
		scale: 1.035,
		transformOrigin: "50% 50%",
	});

	gsap.set([name, title, bio, actions], {
		autoAlpha: 0,
		y: 18,
		filter: "blur(8px)",
	});

	gsap.set(nav, {
		autoAlpha: 0,
		scale: 0.88,
		filter: "blur(8px)",
	});

	const tl = gsap.timeline({
		defaults: {
			ease: "power3.out",
		},
	});

	tl.to(blob, {
		autoAlpha: 1,
		x: 0,
		y: 0,
		scale: 1,
		rotate: 0,
		filter: "blur(0px)",
		duration: 1.45,
		ease: "power3.out",
	})
		.to(
			imageWrap,
			{
				autoAlpha: 1,
				x: 0,
				y: 0,
				scale: 1,
				filter: "blur(0px)",
				duration: 1.15,
				ease: "power3.out",
			},
			0.15
		)
		.to(
			image,
			{
				scale: 1,
				duration: 1.5,
				ease: "sine.inOut",
			},
			0.15
		)
		.to(
			name,
			{
				autoAlpha: 1,
				y: 0,
				filter: "blur(0px)",
				duration: 1.05,
				ease: "power4.out",
			},
			0.35
		)
		.to(
			title,
			{
				autoAlpha: 1,
				y: 0,
				filter: "blur(0px)",
				duration: 0.85,
				ease: "power3.out",
			},
			0.65
		)
		.to(
			bio,
			{
				autoAlpha: 1,
				y: 0,
				filter: "blur(0px)",
				duration: 0.9,
				ease: "power3.out",
			},
			0.9
		)
		.to(
			actions,
			{
				autoAlpha: 1,
				y: 0,
				filter: "blur(0px)",
				duration: 0.75,
				ease: "power3.out",
			},
			1.1
		)
		.to(
			nav,
			{
				autoAlpha: 1,
				scale: 1,
				filter: "blur(0px)",
				duration: 0.65,
				stagger: 0.08,
				ease: "power3.out",
			},
			1.2
		);
})();

/**
 * Inventiva publications year filters.
 */
document.addEventListener("DOMContentLoaded", () => {
	document.querySelectorAll("[data-publications]").forEach((section) => {
		const buttons = Array.from(section.querySelectorAll("[data-publications-year]"));
		const panels = Array.from(section.querySelectorAll("[data-publications-panel]"));

		buttons.forEach((button) => {
			button.addEventListener("click", () => {
				if (button.disabled) {
					return;
				}

				const year = button.getAttribute("data-publications-year");

				buttons.forEach((item) => {
					const isActive = item === button;
					item.classList.toggle("is-active", isActive);
					item.setAttribute("aria-selected", isActive ? "true" : "false");
				});

				panels.forEach((panel) => {
					const isActive = panel.getAttribute("data-publications-panel") === year;
					panel.classList.toggle("is-active", isActive);
					panel.hidden = !isActive;
				});
			});
		});
	});
});

/**
 * Inventiva Investors mega menu hover control.
 * Opens only from the top-level Investors trigger, so the fixed panel cannot
 * appear when the cursor starts in the page area below the header.
 */
document.addEventListener("DOMContentLoaded", function () {
	const megaItem = document.querySelector(
		".inventiva-site-header__nav > ul > .inventiva-menu-mega"
	);

	if (!megaItem) {
		return;
	}

	const megaLink = megaItem.querySelector(":scope > a");
	const megaPanel = megaItem.querySelector(":scope > .sub-menu");

	if (!megaLink || !megaPanel) {
		return;
	}

	let closeTimer = null;

	const openMega = () => {
		window.clearTimeout(closeTimer);
		megaItem.classList.add("is-mega-open");
	};

	const closeMega = () => {
		window.clearTimeout(closeTimer);

		closeTimer = window.setTimeout(() => {
			if (!megaItem.matches(":hover") && !megaItem.contains(document.activeElement)) {
				megaItem.classList.remove("is-mega-open");
			}
		}, 120);
	};

	megaLink.addEventListener("mouseenter", openMega);
	megaLink.addEventListener("focus", openMega);
	megaPanel.addEventListener("mouseenter", () => {
		if (megaItem.classList.contains("is-mega-open")) {
			openMega();
		}
	});
	megaPanel.addEventListener("mouseleave", closeMega);
	megaItem.addEventListener("mouseleave", closeMega);

	document.addEventListener("keydown", (event) => {
		if (event.key === "Escape") {
			megaItem.classList.remove("is-mega-open");
			megaLink.blur();
		}
	});
});

/**
 * Inventiva anchor alias support.
 * Creates stable menu anchor targets for sections whose rendered component IDs
 * differ from the final navigation hash.
 */
document.addEventListener('DOMContentLoaded', function () {
	const path = window.location.pathname.replace(/\/+$/, '/') || '/';

	const aliasConfig = {
		'/about/': {
			'our-people': ['Our People', 'People'],
		},
		'/our-science/': {
			'our-approach': ['Our Approach'],
			'clinical-trials': ['Clinical Trials', 'Clinical Highlights'],
		},
		'/our-focus/': {
			'understanding-mash': ['Understanding MASH'],
			'patient-resources': ['Patient Resources'],
			'living-with-mash': ['Living with MASH'],
			'support-and-community': ['Support and Community'],
			'expanded-access-program': ['Expanded Access Program', 'Expanded Access Policy'],
		},
		'/careers-and-culture/': {
			'our-values': ['Our Values', 'What it means to work here'],
			'our-people': ['Our People'],
			'our-purpose': ['Our Purpose'],
			'open-positions': ['Open Positions', 'Job Openings'],
		},
		'/investors/financials/': {
			'regulated-information': ['Regulated Information'],
			'shareholder-meetings': ['Shareholder Meetings'],
			'shareholder-newsletter': ['Your Letter', 'Shareholder Newsletter'],
		},
		'/fr/investisseurs/resultats-financiers/': {
			'regulated-information': ['Information réglementée'],
			'shareholder-meetings': ['Assemblées générales'],
			'shareholder-newsletter': ['Votre Lettre', 'Lettre aux actionnaires'],
		},
		'/investors/stocks/': {
			'stock-quotes-chart': ['Nasdaq', 'Stock Quote', 'Stock Chart'],
			'historical-lookup': ['Historical Lookup'],
			'analyst-coverage': ['Analyst Coverage'],
		},
		'/fr/investisseurs/stock-market-data/': {
			'stock-quotes-chart': ['Nasdaq', 'Cours de l’action', "Cours de l'action", 'Graphique de l’action', "Graphique de l'action"],
			'historical-lookup': ['Recherche historique'],
			'analyst-coverage': ['Couverture des analystes'],
		},
		'/investors/resources/': {
			'email-alerts': ['Email Alerts'],
			'contact-us': ['Contact Us'],
			'social-media-disclosure': ['Social Media Disclosure'],
			'investors-faqs': ['Investors FAQs'],
		},
		'/fr/investisseurs/ressources/': {
			'email-alerts': ['Alertes e-mail'],
			'contact-us': ['Contactez-nous'],
			'social-media-disclosure': ['Communication via les réseaux sociaux'],
			'investors-faqs': ['FAQ investisseurs'],
		},
	};

	const aliases = aliasConfig[path];

	if (!aliases) {
		return;
	}

	const normalize = function (value) {
		return String(value || '')
			.replace(/\s+/g, ' ')
			.trim()
			.toLowerCase();
	};

	const findSectionByHeading = function (labels) {
		const normalizedLabels = labels.map(normalize);

		const headings = Array.from(document.querySelectorAll('h1, h2, h3, h4, .inventiva-section-eyebrow, .inventiva-section-title'));

		for (const heading of headings) {
			if (normalizedLabels.includes(normalize(heading.textContent))) {
				return /^H[34]$/i.test(heading.tagName)
					? heading
					: heading.closest('section, article, .inventiva-section, .inventiva-component, div') || heading;
			}
		}

		return null;
	};

	const insertAliasAnchor = function (id, target) {
		if (!target || document.getElementById(id)) {
			return;
		}

		const anchor = document.createElement('span');
		anchor.id = id;
		anchor.className = 'inventiva-anchor-alias';
		anchor.setAttribute('aria-hidden', 'true');

		target.parentNode.insertBefore(anchor, target);
	};

	Object.entries(aliases).forEach(function ([id, labels]) {
		const target = findSectionByHeading(labels);
		insertAliasAnchor(id, target);
	});

	const currentHash = window.location.hash ? window.location.hash.slice(1) : '';

	if (currentHash && aliases[currentHash]) {
		window.setTimeout(function () {
			const target = document.getElementById(currentHash);

			if (target) {
				target.scrollIntoView({ behavior: 'auto', block: 'start' });
			}
		}, 80);
	}
});

/**
 * Financials: reveal all Quarterly Results cards.
 */
document.addEventListener('click', (event) => {
	const button = event.target.closest('[data-quarterly-results-load-all]');

	if (!button) {
		return;
	}

	const section = button.closest('[data-quarterly-results-section]');

	if (!section) {
		return;
	}

	section.querySelectorAll('[data-quarterly-result-extra][hidden]').forEach((card) => {
		card.hidden = false;
	});

	const loadMoreWrapper = button.closest('.inventiva-financial-section__load-more');

	if (loadMoreWrapper) {
		loadMoreWrapper.remove();
	} else {
		button.remove();
	}
});

/**
 * Inventiva regulated information tabs.
 */
document.addEventListener('DOMContentLoaded', () => {
	const sections = document.querySelectorAll('[data-regulated-information-tabs]');

	sections.forEach((section) => {
		const tabButtons = section.querySelectorAll('[data-regulated-information-tab]');
		const tabPanels = section.querySelectorAll('[data-regulated-information-panel]');

		const activateTab = (tabKey) => {
			tabButtons.forEach((button) => {
				const isActive = button.dataset.regulatedInformationTab === tabKey;
				button.classList.toggle('is-active', isActive);
				button.setAttribute('aria-selected', isActive ? 'true' : 'false');
			});

			tabPanels.forEach((panel) => {
				const isActive = panel.dataset.regulatedInformationPanel === tabKey;
				panel.classList.toggle('is-active', isActive);
				panel.hidden = !isActive;
			});
		};

		tabButtons.forEach((button) => {
			button.addEventListener('click', () => {
				activateTab(button.dataset.regulatedInformationTab);
			});
		});
	});
});

/**
 * Inventiva shareholder meeting year/date tabs.
 */
document.addEventListener('DOMContentLoaded', () => {
	const sections = document.querySelectorAll('[data-shareholder-tabs]');

	sections.forEach((section) => {
		const yearButtons = section.querySelectorAll('[data-shareholder-year]');
		const yearPanels = section.querySelectorAll('[data-shareholder-year-panel]');

		const activateMeeting = (yearPanel, meetingKey) => {
			const dateButtons = yearPanel.querySelectorAll('[data-shareholder-meeting]');
			const meetingPanels = yearPanel.querySelectorAll('[data-shareholder-meeting-panel]');

			dateButtons.forEach((button) => {
				const isActive = button.dataset.shareholderMeeting === meetingKey;
				button.classList.toggle('is-active', isActive);
				button.setAttribute('aria-selected', isActive ? 'true' : 'false');
			});

			meetingPanels.forEach((panel) => {
				const isActive = panel.dataset.shareholderMeetingPanel === meetingKey;
				panel.classList.toggle('is-active', isActive);
				panel.hidden = !isActive;
			});
		};

		const activateYear = (year) => {
			yearButtons.forEach((button) => {
				const isActive = button.dataset.shareholderYear === year;
				button.classList.toggle('is-active', isActive);
				button.setAttribute('aria-selected', isActive ? 'true' : 'false');
			});

			yearPanels.forEach((panel) => {
				const isActive = panel.dataset.shareholderYearPanel === year;
				panel.classList.toggle('is-active', isActive);
				panel.hidden = !isActive;

				if (isActive) {
					const firstDateButton = panel.querySelector('[data-shareholder-meeting]');
					const firstMeetingPanel = panel.querySelector('[data-shareholder-meeting-panel]');

					if (firstDateButton) {
						activateMeeting(panel, firstDateButton.dataset.shareholderMeeting);
					} else if (firstMeetingPanel) {
						firstMeetingPanel.hidden = false;
						firstMeetingPanel.classList.add('is-active');
					}
				}
			});
		};

		yearButtons.forEach((button) => {
			button.addEventListener('click', () => {
				activateYear(button.dataset.shareholderYear);
			});
		});

		yearPanels.forEach((panel) => {
			const dateButtons = panel.querySelectorAll('[data-shareholder-meeting]');

			dateButtons.forEach((button) => {
				button.addEventListener('click', () => {
					activateMeeting(panel, button.dataset.shareholderMeeting);
				});
			});
		});
	});
});
